import moment from 'moment';
import logger from 'utilities/logger';
import { FormatXlsxDateProps } from './FormatXlsxDate.props';

/**
 * Converts Excel serial date number to moment object (timezone-safe)
 */
const convertExcelSerialToMoment = (serial: number): moment.Moment => {
  // Use UTC to ensure timezone-safe conversion
  if (serial < 1) {
    return moment.utc('1900-01-01');
  }

  // Excel serial date 1 = January 1, 1900
  // Excel serial date 60 = February 29, 1900 (non-existent in real calendars, but Excel includes it)
  // Correct handling:
  //  - Base date: 1899-12-31 (so serial 1 maps to 1900-01-01)
  //  - For serial > 60, subtract 1 day to account for Excel's leap year bug
  //  - For serial === 60, return the fictitious 1900-02-29
  const base = moment.utc('1899-12-31');

  if (serial === 60) {
    return moment.utc('1900-02-29');
  }

  const daysToAdd = serial > 60 ? serial - 1 : serial;
  return base.clone().add(daysToAdd, 'days');
};

/**
 * Converts various input types to a moment object
 */
const convertToMoment = (value: any): moment.Moment | null => {
  if (moment.isMoment(value)) {
    return value;
  }

  if (value instanceof Date) {
    return moment(value);
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }
    return convertExcelSerialToMoment(value);
  }

  if (typeof value === 'string') {
    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
  }

  return null;
};

/**
 * Applies Excel date format using moment.js format strings
 */
const applyDateFormatWithMoment = (momentDate: moment.Moment, format: string): string => {
  // Clean up the format string (handle quotes, escapes, etc.)
  const cleanFormat = format
    // remove locale codes like [$-F800] or [$-409]
    .replace(/\[\$-[^\]]*\]/g, '')
    .replace(/"([^"]*)"/g, '$1')
    .replace(/\\(.)/g, '$1')
    .replace(/_./g, ' ');

  // Protect minutes that appear in time contexts like h:MM or hh:M using placeholders
  // Also support minutes when they appear without hours (e.g., MM:ss or M:ss)
  // This prevents the month mappings from converting them and standardizes uppercase M as minutes
  const protectedFormat = cleanFormat
    // Minutes after hours
    .replace(/([hH]{1,2}):([mM]{2})/g, '$1:__MIN2__')
    .replace(/([hH]{1,2}):([mM]{1})/g, '$1:__MIN1__')
    // Minutes before seconds (no hours)
    .replace(/(^|[^A-Za-z])([mM]{2})(?=:[sS]{1,2})/g, '$1__MIN2__')
    .replace(/(^|[^A-Za-z])([mM]{1})(?=:[sS]{1,2})/g, '$1__MIN1__')
    // Pure uppercase minutes tokens used by suite conventions (MM/M)
    .replace(/MM/g, '__MIN2__')
    .replace(/\bM\b/g, '__MIN1__');

  // Convert Excel format codes to moment.js format codes
  let momentFormat = protectedFormat
    // Protect day-name tokens to avoid being altered by 'dd'/'d' replacements
    .replace(/dddd/g, '__DAY4__')
    .replace(/ddd/g, '__DAY3__')

    // Year formats
    .replace(/yyyy/gi, 'YYYY')
    .replace(/yy/gi, 'YY')

    // Month formats - be careful with order to avoid conflicts
    // Use placeholders first to avoid clashing with minutes (uppercase 'M') handling
    .replace(/mmmm/g, 'MMMM')  // Full month name (do this first)
    .replace(/mmm/g, 'MMM')    // Abbreviated month name
    .replace(/mm/g, '__MONTH2__')      // Month with leading zero (placeholder)
    // Fixed regex for single 'm' - avoid conflicts with minutes
    .replace(/\bm\b/g, '__MONTH1__')    // Month without leading zero (placeholder)

    // Day formats
    .replace(/dd/g, 'DD')      // Day with leading zero
    // Fixed regex for single 'd' - avoid conflicts with day names
    .replace(/\bd\b/g, 'D')    // Day without leading zero (word boundary)

    // Time formats
    .replace(/hh/g, 'HH')      // 24-hour format with leading zero
    // Fixed regex for single 'h' - avoid conflicts with hours
    .replace(/\bh\b/g, 'H')    // 24-hour format without leading zero
    // Seconds (case-insensitive to also accept 'SS')
    .replace(/ss/gi, 'ss')

    // AM/PM formatting
    .replace(/AM\/PM/gi, 'A')
    .replace(/A\/P/gi, 'A');

  // Restore protected day-name tokens
  momentFormat = momentFormat
    .replace(/__DAY4__/g, 'dddd')
    .replace(/__DAY3__/g, 'ddd');

  // Handle 12-hour format when AM/PM is present
  if (/A/i.test(momentFormat)) {
    momentFormat = momentFormat
      .replace(/HH/g, 'hh')  // Convert to 12-hour with leading zero
      .replace(/\bH\b/g, 'h');  // Convert to 12-hour without leading zero
  }

  // Restore protected minutes placeholders to moment minute tokens and month placeholders
  momentFormat = momentFormat
    .replace(/__MIN2__/g, 'mm')
    .replace(/__MIN1__/g, 'm')
    // Restore month placeholders after minutes are restored to avoid conflicts
    .replace(/__MONTH2__/g, 'MM')
    .replace(/__MONTH1__/g, 'M');

  return momentDate.format(momentFormat);
};

/**
 * Converts various input types to a moment object
 */
const convertToDate = (value: any): moment.Moment | null => convertToMoment(value);

/**
 * Get month name (full or abbreviated)
 */
const getMonthName = (monthIndex: number, abbreviated: boolean): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const monthsShort = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  return abbreviated ? monthsShort[monthIndex] : months[monthIndex];
};

/**
 * Checks if a format string is a date format
 */
export const isDateFormat = (format: string): boolean => {
  if (!format || format === 'General') {
    return false;
  }

  // Remove quoted strings and escaped characters to avoid false positives
  const cleanFormat = format
    .replace(/\[\$-[^\]]*\]/g, '')
    .replace(/"[^"]*"/g, '')
    .replace(/\\./g, '');

  const dateIndicators = [
    /[dmyh]/i,           // Basic date/time characters
    /AM\/PM/i,           // AM/PM indicator
    /A\/P/i,             // A/P indicator
    /mmm+/i,             // Month names (mmm or mmmm)
    /ddd+/i,             // Day names (ddd or dddd)
  ];

  return dateIndicators.some(indicator => indicator.test(cleanFormat));
};

/**
 * Extended FormatDate function that integrates with your existing FormatNumber
 */
export const FormatDateValue = (value: any, format: string, dataType?: string): string => {
  // Check if this should be treated as a date
  if (dataType === 'd' || isDateFormat(format)) {
    return FormatXlsxDate({ value, format });
  }

  // Not a date, return the original value as string
  return String(value);
};

/**
 * Formats date values from Excel cells based on Excel number format codes
 * Now uses moment.js for better timezone handling and date arithmetic
 */
export const FormatXlsxDate = ({ value, format }: FormatXlsxDateProps): string => {

  // Handle Excel's fictitious serial 60 (1900-02-29) explicitly since moment/Date cannot represent it
  // https://learn.microsoft.com/en-us/answers/questions/5249322/why-does-microsoft-excel-considers-29-02-1900-to-b
  if (typeof value === 'number' && value === 60) {
    return '1900-02-29'; // Return as string since this is a special case
  }
  // Try moment.js first
  const momentDate = convertToMoment(value);

  if (momentDate && momentDate.isValid()) {
    // Handle "General" format or empty format with a sensible default
    if (!format || format === 'General') {
      return momentDate.format('M/D/YYYY');
    }

    try {
      return applyDateFormatWithMoment(momentDate, format);
    } catch (error) {
      // If moment formatting fails, fall back to Date-based approach
      logger.error('Moment.js formatting failed, falling back to Date:', error);
    }
  }

  // Fallback using moment via convertToDate (now moment-based)
  const fallbackMoment = convertToDate(value);

  if (!fallbackMoment || !fallbackMoment.isValid()) {
    return String(value); // Return original value if not a valid date
  }

  // Handle "General" format or empty format with a sensible default
  if (!format || format === 'General') {
    return fallbackMoment.format('M/D/YYYY');
  }

  return applyDateFormatWithMoment(fallbackMoment, format);
};